import Link from "next/link";
import { RotateCcw } from "lucide-react";
import {
  personalitateConfig,
  type FactorScores,
} from "@/data/tests/personalitate";

/**
 * The Big Five result: five scales, no verdict.
 *
 * `TestRunner`'s `BandScale` places one score among coloured bands. The
 * Mini-IPIP has five scores and no bands, and there are no Romanian norms to
 * turn a raw sum into a percentile, so each dimension is drawn as a plain line
 * between its two poles with the marker where the answers put it. Both poles
 * are described every time, because neither end is the good one, and the
 * copy is deliberately not switched at a midpoint: a cut at 12 would be a
 * band the instrument does not define.
 */
export default function ProfileResult({
  scores,
  onRetake,
}: {
  scores: FactorScores;
  onRetake: () => void;
}) {
  const { factors, factorMin: min, factorMax: max } = personalitateConfig;

  return (
    <div className="space-y-4 text-left">
      <section className="rounded-2xl border border-[#264653]/12 bg-white p-6 sm:p-9">
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#2C969B]">
          Profilul tău
        </p>
        <h2 className="mt-4 text-balance font-serif text-[clamp(1.5rem,3.2vw,2.1rem)] font-medium leading-[1.15] tracking-[-0.01em] text-[#264653]">
          Cinci dimensiuni, nu un tip
        </h2>
        <p className="mt-3 text-[16px] leading-relaxed text-[#264653]/80">
          Fiecare scor adună patru răspunsuri și poate fi între {min} și {max}.
          Punctul arată unde se așază răspunsurile tale între cele două capete.
          Nu te compară cu alți oameni și niciun capăt nu e mai bun decât
          celălalt.
        </p>

        <ol className="mt-8 space-y-8">
          {factors.map((f) => {
            const score = scores[f.key];
            const pct = Math.min(97, Math.max(3, ((score - min) / (max - min)) * 100));
            return (
              <li key={f.key} className="border-t border-[#264653]/10 pt-6">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <h3 className="font-serif text-[20px] font-medium leading-snug text-[#264653]">
                    {f.name}
                  </h3>
                  <span className="text-[14px] tabular-nums text-[#264653]/70">
                    <span className="font-serif text-[22px] font-medium text-[#264653]">
                      {score}
                    </span>{" "}
                    pe o scală de la {min} la {max}
                  </span>
                </div>
                <p className="mt-1 text-[14.5px] leading-relaxed text-[#264653]/70">
                  {f.what}
                </p>

                <div
                  className="relative mt-5 h-2 rounded-full bg-[#264653]/10"
                  role="img"
                  aria-label={`${f.name}: ${score}, pe o scală de la ${min} (${f.low.label}) la ${max} (${f.high.label})`}
                >
                  <span aria-hidden className="absolute inset-y-[-3px] left-1/2 w-px bg-[#264653]/20" />
                  <span
                    aria-hidden
                    className="absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-[#2C969B] shadow"
                    style={{ left: `${pct}%` }}
                  />
                </div>

                <div className="mt-4 grid gap-4 sm:grid-cols-2 sm:gap-8">
                  <p className="text-[14px] leading-relaxed text-[#264653]/75">
                    <span className="block font-semibold text-[#264653]">
                      {f.low.label}
                    </span>
                    {f.low.text}
                  </p>
                  <p className="text-[14px] leading-relaxed text-[#264653]/75 sm:text-right">
                    <span className="block font-semibold text-[#264653]">
                      {f.high.label}
                    </span>
                    {f.high.text}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="rounded-2xl border border-[#264653]/12 bg-white p-6 sm:p-9">
        <h3 className="font-serif text-[clamp(1.3rem,2.6vw,1.7rem)] font-medium text-[#264653]">
          Cum să citești profilul
        </h3>
        <ul className="mt-4 border-t border-[#264653]/10">
          {[
            "Patru afirmații pe dimensiune dau o imagine aproximativă. O zi anume sau felul în care ai înțeles o frază pot muta un scor cu câteva puncte.",
            "Scorurile nu sunt procente. Nu există încă date de referință pentru varianta în română, așa că nu îți putem spune cum te situezi față de alți oameni.",
            "Personalitatea se schimbă încet de-a lungul vieții. Dacă refaci testul peste câțiva ani, profilul poate arăta diferit.",
            "Un scor mare la nevrotism nu e o problemă de sănătate mintală. Descrie cât de intens reacționezi emoțional, nu dacă ai o tulburare.",
          ].map((t) => (
            <li
              key={t}
              className="border-b border-[#264653]/10 py-4 text-[15px] leading-relaxed text-[#264653]/85"
            >
              {t}
            </li>
          ))}
        </ul>
        <p className="mt-5 text-[14.5px] leading-relaxed text-[#264653]/70">
          Mai jos pe pagină găsești ce descrie fiecare dimensiune și de unde vine
          chestionarul. Dacă te frământă ceva anume, pe{" "}
          <Link href="/teste" className="font-medium text-[#2C969B] underline underline-offset-2">
            pagina cu teste
          </Link>{" "}
          sunt și chestionare pentru anxietate, burnout și ADHD.
        </p>
      </section>

      <div className="flex justify-center pt-1">
        <button
          type="button"
          onClick={onRetake}
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[14px] font-medium text-[#264653]/65 transition-colors hover:text-[#264653]"
        >
          <RotateCcw className="size-4" />
          Refă testul
        </button>
      </div>
    </div>
  );
}
